import React, { useEffect, useState, useRef, useMemo } from 'react';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/ScrollAnimation';
import { researchAPI } from '../utils/api';
import { useGesture } from '../context/GestureContext';
import Pagination from '../components/Pagination';
import DetailModal from '../components/DetailModal';

const Research = () => {
  const [research, setResearch] = useState({
    areas: [],
    publications: [],
    collaborations: '',
  });
  const [loading, setLoading] = useState(true);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedArea, setSelectedArea] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const itemsPerPage = 6;
  const { lastGesture, cardNavActive } = useGesture();
  const lastProcessedTime = useRef(0);
  const lastOpenPalmTime = useRef(0);

  useEffect(() => {
    researchAPI.get()
      .then((data) => setResearch(data || { areas: [], publications: [], collaborations: '' }))
      .catch((err) => console.error('Failed to fetch research:', err))
      .finally(() => setLoading(false));
  });

  const totalPages = Math.ceil(research.areas.length / itemsPerPage);

  const paginatedAreas = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return research.areas.slice(start, start + itemsPerPage);
  }, [research.areas, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setFocusedIndex(-1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAreaClick = (area) => {
    setSelectedArea({ ...area, modalType: 'research_area' });
    setIsModalOpen(true);
  };

  const handlePublicationClick = (pub) => {
    setSelectedArea({ ...pub, modalType: 'publication' });
    setIsModalOpen(true);
  };

  // Handle gestures for card focus
  useEffect(() => {
    if (lastGesture.time > lastProcessedTime.current && research.areas.length > 0) {
      lastProcessedTime.current = lastGesture.time;

      // ONLY handle card focus if Navigation Mode is toggled to CARDS
      if (!cardNavActive) return;

      let newIndex = focusedIndex;
      switch (lastGesture.name) {
        case 'swipe_left':
          newIndex = focusedIndex === -1 ? 0 : Math.min(research.areas.length - 1, focusedIndex + 1);
          break;
        case 'swipe_right':
          newIndex = focusedIndex === -1 ? 0 : Math.max(0, focusedIndex - 1);
          break;
        case 'rock_on':
          const now = Date.now();
          if (now - lastOpenPalmTime.current < 1500) return;

          if (isModalOpen) {
            setIsModalOpen(false);
            lastOpenPalmTime.current = now;
          } else if (cardNavActive && focusedIndex !== -1 && paginatedAreas[focusedIndex]) {
            handleAreaClick(paginatedAreas[focusedIndex]);
            lastOpenPalmTime.current = now;
          }
          return;
        default:
          return;
      }

      setFocusedIndex(newIndex);

      // Auto-scroll the focused card into center view
      setTimeout(() => {
        const element = document.getElementById(`research-card-${newIndex}`);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
        }
      }, 50);
    }
  }, [lastGesture, research.areas, cardNavActive, focusedIndex, isModalOpen, paginatedAreas]);

  return (
    <div className="page-container">
      <h1 className="page-title">Research & Innovation</h1>
      <div className="page-content">
        <FadeIn>
          <div className="section">
            <h2>Current Research Areas</h2>
            <p>
              Our research spans multiple domains of artificial intelligence, from fundamental
              algorithms to practical applications that solve real-world problems.
            </p>
          </div>
        </FadeIn>

        <StaggerContainer staggerDelay={0.15}>
          <div className="card-grid" id="card-grid-section">
            {loading && (
              <div className="card">
                <h3>Loading research areas...</h3>
                <p>Fetching the latest research focus areas.</p>
              </div>
            )}
            {!loading && research.areas.length === 0 && (
              <div className="card">
                <h3>No research areas yet</h3>
                <p>Run the seed script to load sample research data.</p>
              </div>
            )}
            {paginatedAreas.map((area, index) => (
              <StaggerItem key={area.title}>
                <div
                  id={`research-card-${index}`}
                  className={`card ${focusedIndex === index ? 'focused' : ''}`}
                  onClick={() => handleAreaClick(area)}
                >
                  <div className="card-modal-indicator">ℹ️ Info</div>
                  <h3>{area.icon} {area.title}</h3>
                  <p>{area.description}</p>
                </div>
              </StaggerItem>
            ))}
          </div>
        </StaggerContainer>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />

        <DetailModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          data={selectedArea}
          type={selectedArea?.modalType || 'research_area'}
        />

        <FadeIn direction="up" delay={0.5}>
          <div className="section">
            <h2>Recent Publications</h2>
            {research.publications.length === 0 ? (
              <p>No publications available yet.</p>
            ) : (
              <ul className="publication-list">
                {research.publications.map((pub) => (
                  <li
                    key={`${pub.title}-${pub.year}`}
                    className="publication-item"
                    onClick={() => handlePublicationClick(pub)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="pub-title">"{pub.title}"</span>
                    <span className="pub-meta"> - {pub.venue}, {pub.year}</span>
                    <span className="pub-view-more">View Abstract ↗</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </FadeIn>

        <FadeIn direction="up" delay={0.6}>
          <div className="section">
            <h2>Research Collaborations</h2>
            <p>{research.collaborations || 'Collaboration highlights will appear here soon.'}</p>
          </div>
        </FadeIn>
      </div>
    </div>
  );
};

export default Research;
